import { useState, useEffect, useCallback, useRef } from 'react'
import { Delete, X, Lock, Loader2 } from 'lucide-react'
import { classifyBurst } from '@/lib/scanBurst.core'
import { PIN_THROTTLED_TEXT } from '@/lib/discountLimit'
import { useUIStore } from '@/stores/uiStore'
import { useConnectionHealthStore } from '@/stores/connectionHealthStore'

const numpadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace']
/** Longest PIN the numpad accepts; the backend decides what's valid. */
const MAX_PIN_LENGTH = 20

/** A barcode scanner is a keyboard: a scan fired while this dialog is open
 *  would otherwise type its digits into the PIN and press Enter, spending a
 *  PIN attempt (and walking the register into the throttle). Keystrokes that
 *  arrive at scanner cadence are a burst, not typing (same gates as
 *  useBarcodeScanner, with a PIN-sized minimum). */
const BURST_GATES = { minLength: 4, avgTimeByCharMs: 50, maxDurationMs: 1500 }
/** A printable key after this much idle time starts a new burst. */
const BURST_GAP_MS = 100
const SCAN_REFUSED = 'That looked like a barcode scan, not a PIN. Enter the PIN again.'
/** Closing mid-check is allowed only once the till is offline -- by the
 *  browser's signal or the passive connection health (server unreachable,
 *  captive portal), which navigator.onLine misses; otherwise wait for the
 *  answer (every caller bounds it at 15s) so a resolved approval can't land
 *  after the dialog is gone. */
function useCanCloseWhileBusy(): boolean {
  const status = useConnectionHealthStore((s) => s.status)
  const browserOnline = useConnectionHealthStore((s) => s.browserOnline)
  return status === 'offline' || !browserOnline
    || (typeof navigator !== 'undefined' && navigator.onLine === false)
}

/** One value the PIN is about to approve (or unlock). */
export interface ManagerApprovalRow {
  key: string
  title: string
  detail?: string
}

/** Lockout after a throttled PIN (`lockoutMs` on the onSubmit rejection).
 *  Module-level so closing and reopening the dialog doesn't lift it. */
let lockedUntil = 0

function lockoutOf(err: unknown): number {
  const ms = (err as { lockoutMs?: unknown } | null)?.lockoutMs
  return typeof ms === 'number' && ms > 0 ? ms : 0
}

export interface ManagerApprovalDialogProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  subtitle?: string
  submitLabel?: string
  /** Shown when the dialog opens (e.g. the backend's reason a previous PIN
   *  was refused, when re-prompting from the cart banner). Read once per
   *  open: a later change doesn't reset an entry in progress. */
  initialError?: string | null
  /** EVERY value this PIN approves, one row each (the discount dialog: each
   *  gated line with qty/lot/discount, or the receipt value with its base;
   *  Settings/Payout: the single thing it unlocks). The PIN applies to
   *  exactly these. */
  items?: readonly ManagerApprovalRow[]
  /**
   * Verify/use the PIN. Resolve to accept (the dialog closes); reject with
   * an Error to refuse -- its message is shown inline, the PIN is cleared
   * and the manager can try again. An Error carrying `lockoutMs`
   * (lib/discountLimit PinThrottledError) also disables submit for that
   * long. The PIN is never stored by the dialog beyond this call.
   */
  onSubmit: (pin: string) => Promise<void>
}

/**
 * Numpad manager-PIN prompt, server-backed. Used for the Settings / Payout
 * gates (check-manager-access) and for approving an over-limit discount
 * (the PIN rides the cart request; see lib/discountLimit.ts). Stacks above
 * the other modals (z-60) since it opens from inside DiscountModal and
 * ProductModal.
 */
export function ManagerApprovalDialog({
  isOpen,
  onClose,
  title = 'Manager approval',
  subtitle = 'Enter a manager PIN to continue',
  submitLabel = 'Approve',
  initialError = null,
  items = [],
  onSubmit,
}: ManagerApprovalDialogProps) {
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [locked, setLocked] = useState(() => Date.now() < lockedUntil)
  // Close un-greys when the till drops offline mid-check (subscribed, so it
  // re-renders on the change).
  const offline = useCanCloseWhileBusy()
  const offlineRef = useRef(offline)
  offlineRef.current = offline
  // Keyboard burst in progress (see BURST_GATES).
  const burstRef = useRef({ count: 0, start: 0, last: 0 })
  // initialError is snapshotted when the dialog opens (callers derive it
  // from live state, which can change while the manager is typing).
  const initialErrorRef = useRef(initialError)
  initialErrorRef.current = initialError

  // Reset entry whenever the dialog opens, and take focus away from the
  // modal underneath: its focused input would otherwise also receive the
  // digits typed for the PIN.
  useEffect(() => {
    if (!isOpen) return
    setPin('')
    setError(initialErrorRef.current
      ?? (Date.now() < lockedUntil ? PIN_THROTTLED_TEXT : ''))
    setBusy(false)
    busyRef.current = false
    burstRef.current = { count: 0, start: 0, last: 0 }
    const active = document.activeElement as HTMLElement | null
    active?.blur?.()
  }, [isOpen])

  // Throttle lockout: re-enable submit when it runs out.
  useEffect(() => {
    if (!isOpen) return
    const remaining = lockedUntil - Date.now()
    if (remaining <= 0) {
      setLocked(false)
      return
    }
    setLocked(true)
    const t = window.setTimeout(() => setLocked(false), remaining)
    return () => window.clearTimeout(t)
  }, [isOpen, locked])

  const handleKey = useCallback((key: string) => {
    if (busyRef.current) return
    setError('')
    if (key === 'backspace') {
      setPin((prev) => prev.slice(0, -1))
    } else if (key === 'clear') {
      setPin('')
    } else {
      setPin((prev) => (prev.length < MAX_PIN_LENGTH ? prev + key : prev))
    }
  }, [])

  const handleSubmit = useCallback(async () => {
    if (busyRef.current || pin.length === 0) return
    if (Date.now() < lockedUntil) return
    busyRef.current = true
    setBusy(true)
    try {
      await onSubmit(pin)
      setPin('')
      onClose()
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Approval failed')
      setPin('')
      const lockMs = lockoutOf(err)
      if (lockMs > 0) {
        lockedUntil = Date.now() + lockMs
        setLocked(true)
      }
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [pin, onSubmit, onClose])

  const pressNumpad = useCallback((key: string) => {
    // A tap is never part of a scanner burst.
    burstRef.current = { count: 0, start: 0, last: 0 }
    handleKey(key)
  }, [handleKey])

  useEffect(() => {
    if (!isOpen) return
    // Capture phase on window (before the barcode scanner's document
    // listener) + stopPropagation on every key: the modal underneath has its
    // own Enter/Escape handlers, and neither PIN digits nor a scan's Enter
    // may reach the scanner or shortcut handlers behind the dialog.
    const onKeyDown = (e: KeyboardEvent) => {
      // The clerk re-auth overlay stacks above this dialog and also listens
      // on window/capture; stopPropagation can't stop a same-target
      // listener, so stand down while it is up: the clerk's PIN must not
      // become an approval attempt (nor Escape close this dialog).
      if (useUIStore.getState().clerkReauthRequired !== null) return
      e.stopPropagation()
      const now = performance.now()
      const b = burstRef.current
      if (e.key.length === 1) {
        if (b.count === 0 || now - b.last > BURST_GAP_MS) {
          burstRef.current = { count: 1, start: now, last: now }
        } else {
          burstRef.current = { count: b.count + 1, start: b.start, last: now }
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        if (!busyRef.current || offlineRef.current) onClose()
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const scanned = b.count > 0
          && now - b.last <= BURST_GAP_MS
          && classifyBurst(b.count, b.last - b.start, BURST_GATES) === 'scan'
        burstRef.current = { count: 0, start: 0, last: 0 }
        if (scanned) {
          // Drop what the scanner typed; never submit it.
          if (!busyRef.current) {
            setPin('')
            setError(SCAN_REFUSED)
          }
          return
        }
        void handleSubmit()
      } else if (e.key === 'Backspace') {
        e.preventDefault()
        handleKey('backspace')
      } else if (/^[0-9]$/.test(e.key)) {
        e.preventDefault()
        handleKey(e.key)
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [isOpen, handleSubmit, handleKey, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center animate-fade-in">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={busy && !offline ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col animate-scale-in"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 bg-amber-100 rounded-lg">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-500">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy && !offline}
            aria-label="Close"
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {items.length > 0 && (
            <ul
              aria-label="Approving"
              className="mb-5 -mt-1 max-h-40 overflow-y-auto divide-y divide-gray-100 rounded-xl border border-gray-200 bg-gray-50"
            >
              {items.map((row) => (
                <li key={row.key} className="px-3 py-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{row.title}</p>
                  {row.detail && <p className="text-xs text-gray-500">{row.detail}</p>}
                </li>
              ))}
            </ul>
          )}

          {/* ph-mask / ph-no-capture: session replay never sees the
              entry (the dot count is the PIN length). */}
          <div className="flex flex-wrap justify-center gap-2 mb-6 min-h-[20px] ph-mask ph-no-capture">
            {pin.length === 0 ? (
              <span className="text-gray-400 text-sm">Enter PIN</span>
            ) : (
              Array.from({ length: pin.length }).map((_, index) => (
                <div key={index} className="w-3.5 h-3.5 rounded-full bg-green-500 animate-scale-in" />
              ))
            )}
          </div>

          {error && (
            <div role="alert" className="text-center text-red-500 text-sm mb-4 animate-fade-in">{error}</div>
          )}

          {/* ph-no-capture: PostHog click-autocapture records button text
              ("Clicked button with text '4'"), which would make the PIN
              reconstructable from the event stream. On the grid AND each
              key (ClerkPinReauthModal / routes/login.tsx do the same). */}
          <div className="grid grid-cols-3 gap-3 ph-no-capture">
            {numpadKeys.map((key) => {
              if (key === 'clear') {
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => pressNumpad('clear')}
                    disabled={busy}
                    className="ph-no-capture h-16 rounded-xl text-gray-400 text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    Clear
                  </button>
                )
              }
              if (key === 'backspace') {
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => pressNumpad('backspace')}
                    disabled={busy}
                    aria-label="Backspace"
                    className="ph-no-capture h-16 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <Delete className="w-6 h-6" />
                  </button>
                )
              }
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pressNumpad(key)}
                  disabled={busy}
                  className="ph-no-capture h-16 rounded-xl text-2xl font-semibold text-gray-800 hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  {key}
                </button>
              )
            })}
          </div>

          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={pin.length === 0 || busy || locked}
            className="w-full mt-4 h-14 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer"
          >
            {busy && <Loader2 className="w-5 h-5 animate-spin" />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
